<template>
    <div class="search-filter">
        <div class="search-filter__input-group">
            <datepicker
                v-model="rent_start"
                input-class="datepicker-input"
                style="z-index: 4"
                :monday-first="mondayFirst"
                :placeholder=lang.rent_start
                :disabled-dates="datepicker1.disabled"
                :highlighted="datepicker1.highlighted"
                :language="getLocale()"
                @input="onDatepicker1Set"
            />
            <div class="search-filter__time-input">
                <time-selector
                    class="select-form"
                    color="white"
                    :hour="rent_start_hour"
                    :from="regularopeningtime"
                    :to="regularclosingtime"
                    :extendedto="extendedto"
                    :disabledfrom="timepicker1.disabledfrom"
                    :disabledto="timepicker1.disabledto"
                    @change="value => {this.rent_start_hour = value; onTimePickerUpdate();}"
                />
            </div>
        </div>
        <div class="search-filter__input-group">
            <datepicker
                v-model="rent_end"
                input-class="datepicker-input"
                style="z-index: 3"
                :monday-first="mondayFirst"
                :placeholder=lang.rent_end
                :disabled-dates="datepicker2.disabled"
                :highlighted="datepicker2.highlighted"
                :open-date="datepicker2.opendate"
                :disabled="datepicker2.isDisabled"
                :language="getLocale()"
                @input="onDatepicker2Set"
                @changedMonth="datepicker2DisabledDates"
                ref="datePicker2"
            />
            <div class="search-filter__time-input">
                <time-selector
                    class="select-form"
                    color="white"
                    placeholder="time"
                    :hour="rent_end_hour"
                    :from="regularopeningtime"
                    :to="regularclosingtime"
                    :extendedto="extendedto"
                    :disabledfrom="timepicker2.disabledfrom"
                    :disabledto="timepicker2.disabledto"
                    @change="value => {this.rent_end_hour = value; onTimePickerUpdate();}"
                />
            </div>
        </div>
        <div class="search-filter__input-group">
            <CustomSelect
                id="vehicle_type"
                color="white"
                name="vehicle_type"
                icon="car"
                :placeholder="lang.vehicle_type_select"
                :options="options"
                :value="vehicle_type"
                @input="updateField"
            />
        </div>

        <footer class="search-filter__footer">
            <div class="search-filter__footer--start">
                <div class="search-filter__summary">
                    <span>{{ lang.days }}: </span>
                    <span>{{ duration }}</span>
                </div>
            </div>

            <div class="search-filter__footer--end">
                <button
                    type="button"
                    class="btn btn-light btn-lg"
                    :disabled="disabled"
                    @click="submit"
                >
                    {{ lang.search }} <i class="fa fa-search" aria-hidden="true"></i>
                </button>
            </div>
        </footer>
    </div>
</template>

<script>
import Datepicker from 'vuejs-datepicker'
import TimeSelector from './TimeSelector'
import CustomSelect from './CustomDropdown.vue'

import {en, es, de} from 'vuejs-datepicker/dist/locale'
import moment from "moment-timezone";
import { getDateOf as svcGetDateOf, getDatepickerLocale } from '../services/time-service';
import { getEarliestHour as svcGetEarliestHour, applyPastTimeBuffer, getSundayTimepickerCaps, getSameDayEndHourMin, getDisabledHoursForToday } from '../services/schedule-service';
import { getEarliestAllowedEndDate, calculateDuration as svcCalculateDuration } from '../services/datepicker-service';

export default {
    components: {
        Datepicker,
        TimeSelector,
        CustomSelect
    },
    props: {
        lang: {
            required: true,
            type: Object
        },
        vehicle_types: {
            required: true,
            type: Array
        },
        schedule_sunday_end: {
            required: true,
            type: Number
        },
        minreservationdays: {
            required: true,
            type: Number
        },
        preventnextmorningreservations: {
            required: true,
            type: [Boolean],
        },
        blockpastreservation: {
            required: true,
            type: Boolean,
            default: false
        },
        regularopeningtime: {
            required: false,
            type: Number,
            default: 9
        },
        regularclosingtime: {
            required: false,
            type: Number,
            default: 18
        },
        extendedto: {
            required: false,
            type: Number,
            default: false
        },
        serverTime: {
            required: true,
            type: String
        },
        timezone: {
            required: true,
            type: String
        },
    },
    data() {
        const _sm       = moment.tz(this.serverTime, this.timezone);
        const _today    = new Date(_sm.year(), _sm.month(), _sm.date());
        const _tomorrow = (() => { const d = _sm.clone().add(1, 'day'); return new Date(d.year(), d.month(), d.date()); })();
        const _late     = _sm.hour() > this.regularclosingtime - 1;
        return {
            bike: bike,
            rent_start: '',
            rent_start_hour: this.regularclosingtime,
            rent_end: '',
            rent_end_hour: this.regularopeningtime,
            vehicle_type: '',
            duration: '',
            disabled: true,
            mondayFirst: true,
            datepicker1: {
                disabled: {
                    to: _late ? _tomorrow : _today,
                },
                highlighted: {
                    from: '',
                    to: '',
                }
            },
            datepicker2: {
                isDisabled: true,
                disabled: {
                    from: '',
                    to: _late ? _tomorrow : _today,
                },
                highlighted: {
                    from: '',
                    to: '',
                },
                opendate: _today
            },
            timepicker1: {
                from: this.regularopeningtime,
                to: this.extendedto || this.regularclosingtime,
                disabledfrom: 0,
                disabledto: 24,
            },
            timepicker2: {
                from: this.regularopeningtime,
                to: this.extendedto || this.regularclosingtime,
                disabledfrom: 0,
                disabledto: 24,
            },
            locales: {
                en: en,
                es: es,
                de: de
            }
        }
    },
    computed: {
        options() {
            return this.vehicle_types
                .map((value) => ({key: value, text: this.lang.i18n[value]}))
                .filter(({key, text}) => key && text)
        },
        serverTimeMoment() {
            return moment.tz(this.serverTime, this.timezone);
        },
        scheduleConfig() {
            return {
                timezone: this.timezone,
                regularOpeningTime: this.regularopeningtime,
                regularClosingTime: this.regularclosingtime,
                scheduleSundayEnd: this.schedule_sunday_end,
                extendedTo: this.extendedto,
                tenant: null,
                blockPastReservation: this.blockpastreservation,
                preventNextMorningReservations: this.preventnextmorningreservations,
                serverTimeMoment: this.serverTimeMoment,
            };
        },
    },
    methods: {
        onDatepicker1Set(date) {
            const sundayCaps = getSundayTimepickerCaps(this.rent_start, this.scheduleConfig);
            this.timepicker1.to = sundayCaps.to;
            this.timepicker1.disabledto = sundayCaps.disabledTo;
            this.rent_start_hour = this.timepicker1.to < this.rent_start_hour ? this.regularopeningtime : this.rent_start_hour;

            let from = this.getEarliestHour(date);
            from = this.checkifDateIsInThePast(date, from);
            this.timepicker1.from = from;
            this.rent_start_hour = from;

            this.rent_end = '';
            this.rent_end_hour = this.regularopeningtime;
            this.datepicker1.highlighted.from = this.getDateOf(this.rent_start);
            this.datepicker1.highlighted.to = '';

            this.datepicker2.isDisabled = false;
            this.datepicker2.opendate = this.getDateOf(this.rent_start);
            this.timepicker2.from = this.regularopeningtime;
            this.datepicker2.highlighted.from = this.getDateOf(this.rent_start);
            this.datepicker2.highlighted.to = '';

            this.$nextTick(function () {
                this.datepicker2DisabledDates();
                this.disableTimePickerHours();
            })
            this.evalSubmitState();
            this.calculateDuration();
        },

        onDatepicker2Set() {
            const sundayCaps = getSundayTimepickerCaps(this.rent_end, this.scheduleConfig);
            this.timepicker2.to = sundayCaps.to;
            this.timepicker2.disabledto = sundayCaps.disabledTo;
            this.rent_end_hour = this.timepicker2.to < this.rent_end_hour ? this.regularopeningtime : this.rent_end_hour;
            this.datepicker2.highlighted.to = this.getDateOf(this.rent_end);
            this.datepicker1.highlighted.to = this.getDateOf(this.rent_end);
            this.checkTimePickers();
            this.evalSubmitState();
            this.calculateDuration();
        },

        datepicker2DisabledDates() {
            const earliestAllowedEndDate = getEarliestAllowedEndDate(this.rent_start, this.minreservationdays, this.timezone);
            this.datepicker2.disabled.to = this.getDateOf(earliestAllowedEndDate);
        },

        onTimePickerUpdate() {
            this.checkTimePickers();
            this.evalSubmitState();
            this.calculateDuration();
        },

        calculateDuration() {
            this.duration = svcCalculateDuration(this.rent_start, this.rent_start_hour, this.rent_end, this.rent_end_hour) || '';
        },

        getEarliestHour(date) {
            return svcGetEarliestHour(this.rent_start, this.scheduleConfig);
        },

        checkNextDayReservations() {
            if (this.preventnextmorningreservations) {
                this.timepicker1.from = this.getEarliestHour(this.rent_start);
                if (this.rent_start_hour < this.timepicker1.from) {
                    this.rent_start_hour = this.timepicker1.from;
                }
            }
        },

        checkifDateIsInThePast(date, from) {
            return applyPastTimeBuffer(date, from, this.scheduleConfig);
        },

        checkTimePickers() {
            if (this.rent_start && this.rent_end) {
                if (this.getDateOf(this.rent_start).getTime() === this.getDateOf(this.rent_end).getTime()) {
                    this.timepicker2.from = getSameDayEndHourMin(this.rent_start_hour);
                    this.rent_end_hour = this.rent_start_hour >= this.rent_end_hour
                        ? this.timepicker2.from
                        : this.rent_end_hour;
                } else {
                    this.timepicker2.from = this.regularopeningtime;
                }
            }
            this.disableTimePickerHours();
        },

        disableTimePickerHours() {
            const result = getDisabledHoursForToday(this.rent_start, this.rent_end, this.scheduleConfig);
            this.timepicker1.disabledfrom = result.startDisabledFrom;
            this.timepicker2.disabledfrom = result.endDisabledFrom;
        },

        updateField({name, value}) {
            this[name] = value
            this.evalSubmitState()
        },

        evalSubmitState() {
            let cond1 = this.rent_start === "" || this.rent_end === "" || !this.rent_start_hour || !this.rent_end_hour;
            let cond2 = !Date.parse(this.rent_start) || !Date.parse(this.rent_end) || this.vehicle_type === "";
            this.disabled = cond1 || cond2;
        },

        submit() {
            const queryParams = new URLSearchParams({
                type: this.vehicle_type,
                start: window.moment(this.rent_start).format('YYYY-MM-DD') + " " + this.rent_start_hour.toString().padStart(2, '0') + ":00:00",
                end: window.moment(this.rent_end).format('YYYY-MM-DD') + " " + this.rent_end_hour.toString().padStart(2, '0') + ":00:00",
            });

            window.location.href = '/' + bike.locale + '/lanzarote/search?' + queryParams;
        },

        getLocale() {
            return getDatepickerLocale(this.bike.locale);
        },

        getDateOf(date) {
            return svcGetDateOf(date);
        }
    },
    mounted() {
        const params = new URLSearchParams(window.location.search);
        this.vehicle_type = params.has('type') ? params.get('type') : '';
        if (params.has('start')) {
            const start = moment(params.get('start'), 'YYYY-MM-DD H:00:00');
            this.rent_start = start.toDate();
            this.rent_start_hour = start.format('H');
            this.$nextTick(function () {
                const start = moment(params.get('start'), 'YYYY-MM-DD H:00:00');
                this.timepicker1.from = this.checkifDateIsInThePast(start, this.rent_start_hour);
                this.rent_start_hour = this.timepicker1.from;
            });
            this.datepicker1.isDisabled = false;
            this.checkNextDayReservations();
        }
        if (params.has('end')) {
            const end = moment(params.get('end'), 'YYYY-MM-DD H:00:00');
            this.rent_end = end.toDate();
            this.rent_end_hour = end.format('H');
            this.datepicker2.isDisabled = false;
        }

        if(params.has('start') && params.has('end')){
            this.$nextTick(function () {
                const start = moment(params.get('start'), 'YYYY-MM-DD H:00:00');
                const end = moment(params.get('end'), 'YYYY-MM-DD H:00:00');
                if (start.isSame(end, 'day')) {
                    if (this.rent_end_hour <= this.rent_start_hour) {
                        this.rent_end_hour = parseInt(this.rent_start_hour) + 1;
                    }
                }
            });
        }

        this.disableTimePickerHours();
        // Ensure Sunday caps on initial load when params are present
        const hasDistinctExtended = this.extendedto && this.extendedto !== this.regularclosingtime;
        if (this.rent_start) {
            const startIsSunday = moment(this.rent_start).isoWeekday() === 7;
            this.timepicker1.disabledto = (startIsSunday && !hasDistinctExtended) ? this.schedule_sunday_end : 24;
        }
        if (this.rent_end) {
            const endIsSunday = moment(this.rent_end).isoWeekday() === 7;
            this.timepicker2.disabledto = (endIsSunday && !hasDistinctExtended) ? this.schedule_sunday_end : 24;
        }
        this.evalSubmitState();
        this.calculateDuration();
        this.datepicker2DisabledDates();
    }
}
</script>

<style>
.search-filter {
    margin-bottom: 2rem;
    padding: 1rem;
    background-color: black;
    border-radius: 0.25rem;
}

.search-filter__input-group {
    display: flex;
    justify-content: space-between;
    align-items: center;
    background-color: transparent;
    border: 1px solid #fff;
    border-radius: 21px;
    flex: 1;
    font-size: .875rem;
    font-weight: 300;
    height: 2.5rem;
    line-height: 1.5rem;
    outline: none;
    padding: 0.5rem 1.5rem;
    position: relative;
    text-align: left;
}

.search-filter__input-group .vdp-datepicker > div:first-child {
    color: white;
}

.search-filter__input-group + .search-filter__input-group {
    margin-top: 1rem;
}

.search-filter__input-group > *:first-child {
    flex: 1;
}

.search-filter .vdp-datepicker {
    position: relative;
}

.search-filter .vdp-datepicker > div {
    position: relative;
    padding-left: 1.5rem;
}

.search-filter .vdp-datepicker > div:before {
    position: absolute;
    top: 0.3125rem;
    left: 0;
    content: "\f073";
    text-rendering: auto;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    display: inline-block;
    font: normal normal normal 14px/1 FontAwesome;
    font-size: inherit;
}

.search-filter__time-input {
    position: relative;
    padding-left: 1.25rem;
}

.search-filter__time-input:before {
    position: absolute;
    top: 0.3125rem;
    left: 0;
    content: "\f017";
    text-rendering: auto;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    display: inline-block;
    font: normal normal normal 14px/1 FontAwesome;
    font-size: inherit;
    color: white;
}

.search-filter .vdp-datepicker:after {
    position: absolute;
    content: "";
    top: -0.4375rem;
    right: 0.5rem;
    width: 1px;
    height: 2.375rem;
    background-color: white;
}

.search-filter .datepicker-input {
    background-color: transparent;
    background-image: url(data:image/svg+xml;charset=utf-8,%3Csvg xmlns= 'http://www.w3.org/2000/svg' viewBox= '0 0 16 16' %3E%3Cpath fill= 'none' stroke= '#fff' stroke-linecap= 'round' stroke-linejoin= 'round' stroke-width= '2' d= 'm2 5 6 6 6-6' /%3E%3C/svg%3E);
    background-position: right 1rem center;
    background-repeat: no-repeat;
    background-size: 1rem 0.875rem;
    color: white;
    border: none;
    box-shadow: none;
    outline: 0;
    width: 100%;
    font-size: 0.875rem;
    line-height: 1.5;
}

.search-filter .select-form {
    flex: 1;
    height: 100%;
}

.search-filter ::v-deep .dropdown-selected {
    border-bottom: none;
    padding: 0;
}

.search-filter .datepicker-input:focus {
    outline: 0;
}

.search-filter__footer {
    margin-top: 1rem;
    display: flex;
    justify-content: space-between;
    align-items: center;
}

.search-filter__footer .search-filter__footer--end {
    display: flex;
    justify-content: flex-end;
}

.search-filter__summary {
    font-size: 0.75rem;
    color: white;
}
</style>
